/* ─────────────────────────────────────────────────────────────
   PREVIOUS VERSION — antd Row/Col header with a plain search box, delegating
   the whole list to ResouceList.js (which rendered it as a stack of Rows).
   Rebuilt below on the shared CustomTable, with stat tiles, filters and the
   Application Name column. Kept commented rather than deleted, per house
   convention.

import React, { useCallback, useEffect, useState } from "react";
import DashboardView from "../DashboardView";
import { Row, Col, Button, Input } from "antd";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ResourceList from "./ResouceList";
import { getResourcesAsync, getResourceTypesAsync } from "../../redux/resourceSlice";
import Loading from "../../components/common/Spinners/Loading";

const { Search } = Input;

export default function Resources() {
  const [data, setData] = useState([]);
  const [searchValue, setSearchValue] = useState("");

  const resources = useSelector((state) => state.resources);
  const groups = useSelector((state) => state.boards.groups);
  const dispatch = useDispatch();

  const onSearchColName = (value) => {
    setSearchValue(value);
    if (!value || value.trim() === "") {
      return fetchResources();
    }
    const searchTerm = value.trim().toLowerCase().replace(/\s+/g, "");
    const filteredData =
      resources.resourceData?.filter((item) => {
        const name = (item.name || "").toLowerCase().replace(/\s+/g, "");
        const resourceType = (item.resoueceType || "").toLowerCase().replace(/\s+/g, "");
        return name.includes(searchTerm) || resourceType.includes(searchTerm);
      }) || [];
    setData(filteredData);
  };

  ... header Row/Col, Loading, and <ResourceList /> ...
  ... full previous implementation in git history ...
}
   ───────────────────────────────────────────────────────────── */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { message } from "antd";

import ResourceIcon from "../../../src/assets/icons/resources/resource-icon.svg";

import DashboardView from "../DashboardView";
import CustomTable from "../../components/common/DataTable";
import ConfirmDialog from "../../components/common/ConfirmDialog";
/* The antd modal forms are no longer opened from this page — creating and
   editing both go to the resource editor at /resources/new and
   /resources/:id/edit. The forms themselves are untouched and still reachable
   through the older /add-resource flow.
import RestApiForm from "../../components/resourceForms/RestApiForm";
import MongoDBForm from "../../components/resourceForms/MongoDBForm";
import GraphQLForm from "../../components/resourceForms/GraphQLForm";
*/
import {
  getResourcesAsync,
  getResourceTypesAsync,
  updateSelectedResource,
  deletetResourceAsync,
  resetResourceDeleteStatus,
} from "../../redux/resourceSlice";
import "./Resources.css";

/**
 * Resources list — every API connection and data source, with the application
 * each one belongs to.
 *
 * The resource records carry only `appId`, not the application's name (see the
 * shape in the /resource response), so the name is resolved here against the
 * app registry list DashboardView already loads into `state.user.apps`. No API
 * change needed for that.
 *
 * Creating and editing still open the existing antd form modals. The
 * Postman-style resource editor replaces both; this page is the list only.
 */

/* ── Connection types ────────────────────────────────────────
   A resource's type arrives two ways — `icon` ("rest_api") and `resoueceType`
   ("Rest Api", note the misspelling in the API field) — and neither is
   consistent about spacing or case. Both are squashed to one key. */
const typeKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/* The types that can be created. MySQL was in an early pass of the new editor,
   never in the old flow, and has no form behind it — an existing record of that
   type still lists, under its own raw type name. */
const CONNECTION_TYPES = [
  { key: "restapi", label: "REST API" },
  { key: "mongodb", label: "Mongo DB" },
  { key: "graphql", label: "Graph QL" },
];

const TypeIcon = {
  restapi: (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="7.3" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M2.7 10h14.6M10 2.7c1.9 2 2.9 4.6 2.9 7.3s-1 5.3-2.9 7.3c-1.9-2-2.9-4.6-2.9-7.3S8.1 4.7 10 2.7z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  ),
  mongodb: (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true">
      <ellipse cx="10" cy="5" rx="6.3" ry="2.4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3.7 5v10c0 1.3 2.8 2.4 6.3 2.4s6.3-1.1 6.3-2.4V5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3.7 10c0 1.3 2.8 2.4 6.3 2.4s6.3-1.1 6.3-2.4" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  graphql: (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true">
      <circle cx="10" cy="3.6" r="1.8" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="4.2" cy="14" r="1.8" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="15.8" cy="14" r="1.8" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8.6 4.9L5.3 12.3M11.4 4.9l3.3 7.4M6 14h8" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  ),
};

const Icon = {
  plus: (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  caret: (
    <svg viewBox="0 0 12 8" width="10" height="7" fill="none" aria-hidden="true">
      <path d="M1 1l5 5 5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  search: (
    <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden="true">
      <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M13 13l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  /* The glyph on every row — a plug, for "a connection". */
  resource: (
    <svg viewBox="0 0 18 18" width="17" height="17" fill="none" aria-hidden="true">
      <path
        d="M6.4 2.2v3.1M11.6 2.2v3.1M4.6 5.3h8.8v3.4a4.4 4.4 0 01-4.4 4.4 4.4 4.4 0 01-4.4-4.4V5.3zM9 13.1v2.7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  edit: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <path d="M11.2 2.3l2.5 2.5-8 8-3.2.7.7-3.2 8-8z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  ),
  trash: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <path
        d="M2.5 4h11M6 4V2.6h4V4M6.4 7v4.6M9.6 7v4.6M3.6 4l.7 8.6a1.2 1.2 0 001.2 1.1h5a1.2 1.2 0 001.2-1.1L12.4 4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

export default function Resources() {
  const dispatch = useDispatch();
  const history = useHistory();

  const resources = useSelector((state) => state.resources);
  const apps = useSelector((state) => state.user.apps?.Result || []);
  /* `groups`, `usersData` and `resources.resourceTypes` were only here to feed
     the form modals this page used to open. The editor reads them itself now. */

  /* Memoised, not `resources.resourceData || []` inline: the fallback would be
     a fresh array on every render and rebuild every memo below it. */
  const resourceData = useMemo(() => resources.resourceData || [], [resources.resourceData]);
  const isLoading = resources.resourceStatus === "pending" || resources.resourceStatus === "default";

  const [searchQuery, setSearchQuery] = useState("");
  const [filterApp, setFilterApp] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [addMenuOpen, setAddMenuOpen] = useState(false);

  const [pendingDelete, setPendingDelete] = useState(null);

  const fetchResources = useCallback(() => dispatch(getResourcesAsync()), [dispatch]);
  const fetchResourceTypes = useCallback(() => dispatch(getResourceTypesAsync()), [dispatch]);

  useEffect(() => {
    fetchResources();
    fetchResourceTypes();
  }, [fetchResources, fetchResourceTypes]);

  // The delete reducer only flips resourceDeleteStatus, so the list is refetched.
  useEffect(() => {
    if (resources.resourceDeleteStatus === "success") {
      message.success("Resource has been successfully deleted!");
      dispatch(resetResourceDeleteStatus());
      fetchResources();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resources.resourceDeleteStatus]);

  // Clicking anywhere else closes the Add New menu.
  useEffect(() => {
    if (!addMenuOpen) return undefined;
    const onDocumentMouseDown = (e) => {
      if (e.target.closest && e.target.closest(".rsc-add-wrap")) return;
      setAddMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocumentMouseDown);
    return () => document.removeEventListener("mousedown", onDocumentMouseDown);
  }, [addMenuOpen]);

  const appNameById = useMemo(() => new Map(apps.map((a) => [a.ApplicationID, a.Name])), [apps]);

  /* ── One row per record ────────────────────────────────────
     One record is one resource is one URL, so the list is the response with
     nothing folded together.

     An earlier pass collapsed records sharing a `connectionData.groupId` into a
     single row, to present several endpoints as one resource. That is gone: the
     group id was a per-page-load counter, so unrelated resources collided on it
     and disappeared into each other's rows. Records saved during that pass still
     carry groupId and a composed "Resource / Module / Endpoint" name; they list
     correctly as themselves, and renaming one in the editor tidies it up. */
  const rows = useMemo(() => {
    /* Newest first. The API returns them oldest first, so a resource just
       created landed on the last page — the one place nobody looks after
       pressing Create.

       Ordered on `createdAt`, NOT on `created`. They look interchangeable and
       are not: `created` is re-stamped on every update, so editing a resource
       moved it to the top as though it had just been made. A record seen with
       createdAt 09:49 and updatedAt 10:18 came back with `created` at 10:18.
       `createdAt` keeps the original moment, so an edit leaves the row where it
       is — which is what the backend already gives us, with nothing to change
       there.

       A record with no usable `createdAt` falls back to its position in the
       response, which is oldest-first insertion order, and sorts below the
       records that do have one.

       Sorted on a copy: resourceData is the Redux array and reordering it in
       place would mutate the store. */
    const createdAtOf = (resource) => {
      const parsed = Date.parse(resource.createdAt || "");
      return Number.isNaN(parsed) ? null : parsed;
    };

    return resourceData
      .map((resource, index) => ({ resource, index, at: createdAtOf(resource) }))
      .sort((a, b) => {
        if (a.at !== null && b.at !== null && a.at !== b.at) return b.at - a.at;
        if (a.at !== null && b.at === null) return -1;
        if (a.at === null && b.at !== null) return 1;
        // Same instant, or neither dated: later in the response is the newer one.
        return b.index - a.index;
      })
      .map(({ resource, index }) => ({
        key: resource.id || resource._id || index,
        // The record the row acts on: opening, editing and viewing all use it.
        resource,
        name: resource.name || resource.connectionData?.name || "Untitled",
        /* Falls back to the raw id rather than an em dash: an unresolved id is
           a resource pointing at an app this user cannot see, and hiding that
           makes it look like the resource has no app at all. */
        appName: appNameById.get(resource.appId) || resource.appId || "—",
        typeKey: typeKey(resource.icon || resource.resoueceType),
        typeLabel:
          (CONNECTION_TYPES.find((t) => t.key === typeKey(resource.icon || resource.resoueceType)) || {}).label ||
          resource.resoueceType ||
          "Unknown",
      }));
  }, [resourceData, appNameById]);

  /* Counts are of every resource, whatever the filters say. */
  const stats = useMemo(
    () => CONNECTION_TYPES.map((t) => ({ ...t, count: rows.filter((r) => r.typeKey === t.key).length })),
    [rows],
  );

  /* Only apps that actually have a resource — filtering to an empty result is
     noise. "—" is left out too: it is the placeholder the column shows for a
     resource whose application could not be resolved, not an application
     anybody means to filter by. Those rows still show it in the column. */
  const appOptions = useMemo(
    () => [...new Set(rows.map((r) => r.appName))].filter((name) => name !== "—").sort(),
    [rows],
  );

  const filteredRows = useMemo(() => {
    // Spaces are dropped from both sides, so "restapi" finds "Rest Api".
    const q = searchQuery.trim().toLowerCase().replace(/\s+/g, "");
    return rows.filter((row) => {
      const matchSearch =
        q === "" ||
        row.name.toLowerCase().replace(/\s+/g, "").includes(q) ||
        row.typeLabel.toLowerCase().replace(/\s+/g, "").includes(q);
      const matchApp = filterApp === "all" || row.appName === filterApp;
      const matchType = filterType === "all" || row.typeKey === filterType;
      return matchSearch && matchApp && matchType;
    });
  }, [rows, searchQuery, filterApp, filterType]);

  /* Same gate the old list used: everyone sees the actions, but a Witmeg-owned
     resource is read-only unless you are a special user. */
  const isSpecialUser = localStorage.getItem("IsSpecialUser") === "true";
  const isLocked = (resource) => Boolean(resource.isWitmegApi) && !isSpecialUser;

  const openResource = (row) => {
    dispatch(updateSelectedResource(row.resource));
    history.push(`/report-view/${row.resource.id}`);
  };

  /* Editing opens the resource editor on exactly this record — pressing the
     button there updates it and creates nothing. */
  const openEditor = (row) => {
    if (!CONNECTION_TYPES.some((t) => t.key === row.typeKey)) {
      message.error("That connection type is not configurable yet.");
      return;
    }
    history.push(`/resources/${row.resource.id}/edit`);
  };

  /* The type rides along in the query — the editor needs it to know which
     layout to open, and it is the one thing that cannot be changed afterwards. */
  const startCreate = (type) => {
    setAddMenuOpen(false);
    history.push(`/resources/new?type=${encodeURIComponent(type.key)}`);
  };

  /* One row is one record, so one delete call. */
  const confirmDelete = () => {
    const row = pendingDelete;
    setPendingDelete(null);
    if (!row) return;
    dispatch(deletetResourceAsync({ id: row.resource.id }));
  };

  const columns = [
    /* Widths are set on three of the four columns so the name stops swallowing
       the slack. With table-layout auto the browser hands leftover space to
       whichever column has the widest content, and resource names are long, so
       without a width here the name column took almost all of it. */
    {
      title: "Resource Name",
      dataIndex: "name",
      key: "name",
      width: "40%",
      render: (value, row) => (
        <div className="rsc-name-cell">
          <img src={ResourceIcon} alt="Resource" className="rsc-name-icon" />
          {/* <span className="rsc-name-icon">{Icon.resource}</span> */}
          <span
            className="rsc-name-text"
            // onClick={() => openResource(row)}
          >
            {value}
          </span>
        </div>
      ),
    },
    { title: "Application Name", dataIndex: "appName", key: "appName", width: "30%", muted: true },
    {
      title: "Connection Type",
      dataIndex: "typeLabel",
      key: "typeLabel",
      width: "22%",
      nowrap: true,
      render: (value, row) => <span className={`rsc-type-pill rsc-type-${row.typeKey}`}>{value}</span>,
    },
    {
      title: "Action",
      key: "action",
      width: 110,
      nowrap: true,
      render: (_value, row) => {
        const locked = isLocked(row.resource);
        return (
          <div className="rsc-actions">
            <button
              type="button"
              className="rsc-action-btn"
              title={locked ? "Witmeg resources are read-only" : "Edit resource"}
              disabled={locked}
              onClick={() => openEditor(row, false)}
            >
              {Icon.edit}
            </button>
            <button
              type="button"
              className="rsc-action-btn rsc-action-danger"
              title={locked ? "Witmeg resources cannot be deleted" : "Delete resource"}
              disabled={locked}
              onClick={() => setPendingDelete(row)}
            >
              {Icon.trash}
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <DashboardView>
      <div className="rsc-shell">
        {/* Header */}
        <div className="rsc-header">
          <div>
            <h1 className="rsc-title">Resources</h1>
            <p className="rsc-subtitle">Manage API connections and data sources</p>
          </div>

          <div className="rsc-add-wrap">
            <button
              type="button"
              className="rsc-add-btn"
              aria-expanded={addMenuOpen}
              aria-haspopup="menu"
              onClick={() => setAddMenuOpen((o) => !o)}
            >
              {Icon.plus}
              Add New
              <span className="rsc-add-caret">{Icon.caret}</span>
            </button>

            {addMenuOpen && (
              <div className="rsc-add-menu" role="menu">
                <div className="rsc-add-menu-head">Connection Type</div>
                {CONNECTION_TYPES.map((type) => (
                  <button
                    type="button"
                    role="menuitem"
                    className="rsc-add-option"
                    key={type.key}
                    onClick={() => startCreate(type)}
                  >
                    <span className={`rsc-name-icon rsc-type-${type.key}`} style={{ background: "transparent" }}>
                      {TypeIcon[type.key]}
                    </span>
                    {type.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Stat cards */}
        <div className="rsc-stats">
          {stats.map((stat) => (
            <div className="rsc-stat-card" key={stat.key}>
              <span className={`rsc-stat-icon rsc-type-${stat.key}`}>{TypeIcon[stat.key]}</span>
              <div>
                <div className="rsc-stat-value">{stat.count}</div>
                <div className="rsc-stat-label">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="rsc-controls">
          <div className="rsc-search-wrap">
            <span className="rsc-search-icon">{Icon.search}</span>
            <input
              className="rsc-search-input"
              type="text"
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="rsc-filters">
            <select className="rsc-select" value={filterApp} onChange={(e) => setFilterApp(e.target.value)}>
              <option value="all">Application</option>
              {appOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>

            <select className="rsc-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="all">Connection Type</option>
              {CONNECTION_TYPES.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <CustomTable
          tableHeaders={columns}
          tableData={filteredRows}
          loading={isLoading}
          emptyText="No matching resources found."
          minWidth={760}
          resetPageOn={`${searchQuery}|${filterApp}|${filterType}`}
        />

        <ConfirmDialog
          open={Boolean(pendingDelete)}
          danger
          title="Delete this resource?"
          message={`"${pendingDelete?.name || "This resource"}" will be removed permanently, and any report using it stops working. This cannot be undone.`}
          confirmText="Delete"
          onCancel={() => setPendingDelete(null)}
          onConfirm={confirmDelete}
        />
      </div>

      {/* ─────────────────────────────────────────────────────────────
          The antd form modals this page used to open for edit and view.
          Replaced by the resource editor at /resources/:id/edit, which handles
          every connection type in one page. Kept commented rather than deleted,
          per house convention — the forms themselves are still live under
          /add-resource.

      {editing ? (
        <Modal
          title={`${editing.isViewOnly ? "" : "Update "}${
            editing.resource.icon === "graphql" ? "GraphQL" : editing.resource.resoueceType
          }${editing.isViewOnly ? " Overview" : ""}`}
          visible
          onOk={() => setEditing(null)}
          onCancel={() => setEditing(null)}
          footer={null}
          width={768}
        >
          {(() => {
            const shared = {
              resourceType: editing.resourceType,
              handleClose: () => {
                setEditing(null);
                fetchResources();
              },
              appList: [editing.resource.appId],
              appsMultiSelectEnabled: false,
              usersData,
              groupList: groups,
              selectedResource: editing.resource,
              isViewOnly: editing.isViewOnly,
              clearSearch: () => setSearchQuery(""),
            };
            switch (editing.resourceType.formName) {
              case "RestApiForm":
                return <RestApiForm {...shared} />;
              case "MongoDBForm":
                return <MongoDBForm {...shared} />;
              case "GraphQLForm":
                return <GraphQLForm {...shared} />;
              default:
                return (
                  <Alert
                    message={`${editing.resourceType.name} Configuration Not Allowed`}
                    description={`Currently not allowed to configure ${editing.resourceType.name}.`}
                    type="info"
                  />
                );
            }
          })()}
        </Modal>
      ) : null}
          ───────────────────────────────────────────────────────────── */}
    </DashboardView>
  );
}
